import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/* ── DeleteConfirmBar ──────────────────────────────────────────── */
interface DeleteConfirmBarProps {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export const DeleteConfirmBar: React.FC<DeleteConfirmBarProps> = ({
  name,
  onConfirm,
  onCancel,
  isDeleting,
}) => {
  const { darkMode } = useTheme();
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div
      ref={barRef}
      className={`flex items-center justify-between px-3 py-2 rounded-lg animate-in fade-in duration-150 ${
        darkMode ? 'bg-red-500/15' : 'bg-red-50'
      }`}
    >
      <span className={`text-xs font-medium truncate mr-3 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
        Delete {name}?
      </span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
            darkMode
              ? 'text-slate-300 hover:bg-slate-700'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className={`px-2.5 py-1 text-xs rounded-md font-semibold text-white transition-colors ${
            isDeleting ? 'bg-red-400 cursor-not-allowed opacity-60' : 'bg-red-500 hover:bg-red-600'
          }`}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  );
};

/* ── DeleteConfirmRow ──────────────────────────────────────────── */
interface DeleteConfirmRowProps {
  colSpan: number;
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export const DeleteConfirmRow: React.FC<DeleteConfirmRowProps> = ({
  colSpan,
  name,
  onConfirm,
  onCancel,
  isDeleting,
}) => {
  const { darkMode } = useTheme();

  return (
    <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}>
      <td colSpan={colSpan} className="py-1 px-0">
        <DeleteConfirmBar
          name={name}
          onConfirm={onConfirm}
          onCancel={onCancel}
          isDeleting={isDeleting}
        />
      </td>
    </tr>
  );
};

/* ── EditModeToggle ────────────────────────────────────────────── */
interface EditModeToggleProps {
  isEditMode: boolean;
  onToggle: () => void;
  itemCount: number;
  disabled?: boolean;
}

export const EditModeToggle: React.FC<EditModeToggleProps> = ({
  isEditMode,
  onToggle,
  itemCount,
  disabled,
}) => {
  const { darkMode } = useTheme();

  if (itemCount === 0) return null;

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`p-1.5 rounded-lg transition-all ${
        isEditMode
          ? darkMode
            ? 'text-violet-400 bg-violet-500/20'
            : 'text-violet-600 bg-violet-100'
          : darkMode
            ? 'text-slate-400 hover:text-violet-400 hover:bg-violet-500/20'
            : 'text-slate-500 hover:text-violet-600 hover:bg-violet-100'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      title={isEditMode ? 'Done editing' : 'Edit items'}
    >
      {isEditMode ? <Check size={18} /> : <Pencil size={18} />}
    </button>
  );
};

/* ── EditModeCheckbox ──────────────────────────────────────────── */
interface EditModeCheckboxProps {
  checked: boolean;
  onToggle: () => void;
}

export const EditModeCheckbox: React.FC<EditModeCheckboxProps> = ({ checked, onToggle }) => {
  const { darkMode } = useTheme();

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition-all ${
        checked
          ? 'bg-violet-500 border-violet-500 text-white'
          : darkMode
            ? 'border-slate-600 hover:border-violet-400'
            : 'border-slate-300 hover:border-violet-500'
      }`}
    >
      {checked && <Check size={14} strokeWidth={3} />}
    </button>
  );
};

/* ── EditModeActionBar ─────────────────────────────────────────── */
interface EditModeActionBarProps {
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDeleteSelected: () => Promise<void>;
  isDeleting: boolean;
}

export const EditModeActionBar: React.FC<EditModeActionBarProps> = ({
  selectedCount,
  totalCount,
  allSelected,
  onSelectAll,
  onDeselectAll,
  onDeleteSelected,
  isDeleting,
}) => {
  const { darkMode } = useTheme();
  const [confirming, setConfirming] = useState(false);

  const handleDeleteClick = () => {
    if (confirming) {
      onDeleteSelected();
      setConfirming(false);
    } else {
      setConfirming(true);
    }
  };

  const handleCancel = () => setConfirming(false);

  const hasSelection = selectedCount > 0;

  return (
    <div className={`flex items-center justify-between py-2 mb-1 border-b transition-colors ${
      hasSelection
        ? darkMode ? 'border-violet-500/30' : 'border-violet-200'
        : darkMode ? 'border-white/5' : 'border-slate-100'
    }`}>
      <button
        onClick={allSelected ? onDeselectAll : onSelectAll}
        className={`flex items-center gap-2 text-sm font-medium ${
          darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'
        }`}
      >
        <span className={`w-5 h-5 rounded flex items-center justify-center border transition-all ${
          allSelected
            ? 'bg-violet-500 border-violet-500 text-white'
            : darkMode
              ? 'border-slate-600'
              : 'border-slate-300'
        }`}>
          {allSelected && <Check size={14} strokeWidth={3} />}
        </span>
        {allSelected ? 'Deselect All' : 'Select All'}
      </button>

      <div className="flex items-center gap-2">
        {confirming && (
          <button
            onClick={handleCancel}
            className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
              darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleDeleteClick}
          disabled={selectedCount === 0 || isDeleting}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-semibold transition-all ${
            selectedCount === 0 || isDeleting
              ? 'opacity-40 cursor-not-allowed'
              : confirming
                ? 'bg-red-500 text-white hover:bg-red-600'
                : darkMode
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
          }`}
        >
          <Trash2 size={14} />
          {isDeleting
            ? 'Deleting...'
            : confirming
              ? `Delete ${selectedCount} item${selectedCount !== 1 ? 's' : ''}?`
              : `Delete Selected (${selectedCount})`}
        </button>
      </div>
    </div>
  );
};
